import { http } from '@/api/http';
import { unwrap, type SuccessResponseDTO } from '@/api/envelope';
import type { StickerReleaseManifest } from './stickerImport';

const BASE = '/admin/api/media/stickers/releases';
export type StickerReleaseStatusName =
  'STAGING' | 'VALIDATING' | 'READY' | 'PUBLISHING' | 'PUBLISHED' | 'FAILED' | 'RETIRED';
export interface StickerReleaseStatus {
  releaseId: string;
  status: StickerReleaseStatusName;
  expectedCount: number;
  sourceUploadedCount: number;
  thumbnailUploadedCount: number;
  verifiedAssetCount?: number;
  expectedAssetCount?: number;
  missingAssets: { stickerId: string; sourceUploaded: boolean; thumbnailUploaded: boolean }[];
  error?: string | null;
  publicationId?: string | null;
  version: string;
}

export interface StickerPublication {
  publicationId: string;
  releaseId?: string | null;
  operation: string;
  status: string;
  error?: string | null;
  version: string;
  createdAt: string;
  updatedAt: string;
}

export const stickerReleases = {
  async list(): Promise<StickerReleaseStatus[]> {
    return unwrap((await http.get<SuccessResponseDTO<StickerReleaseStatus[]>>(BASE)).data);
  },
  async create(manifest: StickerReleaseManifest): Promise<StickerReleaseStatus> {
    return unwrap((await http.post<SuccessResponseDTO<StickerReleaseStatus>>(BASE, manifest)).data);
  },
  async status(releaseId: string): Promise<StickerReleaseStatus> {
    return unwrap(
      (
        await http.get<SuccessResponseDTO<StickerReleaseStatus>>(
          `${BASE}/${encodeURIComponent(releaseId)}`,
        )
      ).data,
    );
  },
  async upload(
    releaseId: string,
    stickerId: string,
    kind: 'source' | 'thumbnail',
    file: File,
  ): Promise<void> {
    await http.put(
      `${BASE}/${encodeURIComponent(releaseId)}/assets/${encodeURIComponent(stickerId)}/${kind}`,
      file,
      { headers: { 'Content-Type': 'image/png' } },
    );
  },
  async action(
    releaseId: string,
    action: 'validate' | 'activate' | 'retry' | 'rollback',
  ): Promise<StickerReleaseStatus> {
    return unwrap(
      (
        await http.post<SuccessResponseDTO<StickerReleaseStatus>>(
          `${BASE}/${encodeURIComponent(releaseId)}/${action}`,
        )
      ).data,
    );
  },
  async publications(): Promise<StickerPublication[]> {
    return unwrap(
      (
        await http.get<SuccessResponseDTO<StickerPublication[]>>(
          '/admin/api/media/stickers/publications',
        )
      ).data,
    );
  },
  async retryPublication(publicationId: string): Promise<StickerPublication[]> {
    return unwrap(
      (
        await http.post<SuccessResponseDTO<StickerPublication[]>>(
          `/admin/api/media/stickers/publications/${encodeURIComponent(publicationId)}/retry`,
        )
      ).data,
    );
  },
};
